import React, { useEffect, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import { Placement } from 'react-bootstrap/esm/types';
import { FormattedMessage } from 'react-intl';

export interface MultiEntityButtonProps<T> {
    entities: T[];
    render: (entity: T) => React.ReactNode;
    placement?: Placement;
}

const OVERLAY_SELECTOR = '[data-multi-entity-trigger], [data-multi-entity-overlay]';

/**
 * Fixed strategy avoids expanding modal/page scrollbars.
 * Flip + altAxis preventOverflow keep the card readable near viewport edges
 * (e.g. receivers on the right → shifts/flips instead of clipping off-screen).
 */
export const MULTI_ENTITY_POPPER_CONFIG = {
    strategy: 'fixed' as const,
    modifiers: [
        {
            name: 'flip',
            options: {
                fallbackPlacements: ['bottom-end', 'bottom-start', 'top', 'top-end', 'top-start', 'left', 'right']
            }
        },
        {
            name: 'preventOverflow',
            options: {
                boundary: 'viewport',
                altAxis: true,
                padding: 8
            }
        }
    ]
};

export const MultiEntityButton = <T,>(props: MultiEntityButtonProps<T>) => {
    const [show, setShow] = useState(false);
    const [trigger, setTrigger] = useState<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!show) {
            return;
        }
        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Element | null;
            if (target?.closest?.(OVERLAY_SELECTOR)) {
                return;
            }
            setShow(false);
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [show]);

    return (
        <>
            <div
                ref={setTrigger}
                data-multi-entity-trigger=''
                onClick={(e) => {
                    e.stopPropagation();
                    setShow((open) => !open);
                }}
                data-popover-target='popover-default'
                className='bg-border-default hover:bg-background-surface inline-flex h-6 items-center justify-start gap-2.5 rounded-sm px-1 hover:cursor-pointer'>
                <div className='text-foreground-default justify-start text-sm font-medium'>
                    <FormattedMessage
                        id='common.more'
                        defaultMessage='+{count} more'
                        values={{ count: props.entities.length }}
                    />
                </div>
            </div>
            <Overlay
                target={trigger}
                show={show}
                placement={props.placement || 'bottom'}
                flip
                transition={false}
                container={typeof document !== 'undefined' ? document.body : undefined}
                popperConfig={MULTI_ENTITY_POPPER_CONFIG}>
                {(overlayProps) => (
                    <Tooltip
                        {...overlayProps}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        className='border-border-default bg-background-surface z-1000 flex max-w-[min(20rem,calc(100vw-1rem))] flex-col rounded-xl border'
                        id='multi-entity-tooltip'
                        data-multi-entity-overlay=''>
                        <div
                            data-testid='multi-entity-overlay'
                            data-multi-entity-overlay=''
                            className='flex max-h-60 flex-col overflow-y-auto p-4'>
                            {props.entities.map((e, index) => (
                                <span key={index}>{props.render(e)}</span>
                            ))}
                        </div>
                    </Tooltip>
                )}
            </Overlay>
        </>
    );
};
